import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import api from "../lib/api";
import { formatDate, formatPct, statusColor } from "../utils/format";
import { ArrowLeft, ExternalLink, Globe, Sparkles, Edit2, Save } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";

export default function LandingPageDetail() {
  const { id } = useParams();
  const [lp, setLp] = useState(null);
  const [editing, setEditing] = useState(false);
  const [notes, setNotes] = useState("");
  const [healthScore, setHealthScore] = useState(80);

  const load = async () => {
    const { data } = await api.get(`/landing-pages/${id}`);
    setLp(data);
    setNotes(data.notes || "");
    setHealthScore(data.health_score || 80);
  };

  useEffect(() => { load(); }, [id]);

  const save = async () => {
    try {
      await api.patch(`/landing-pages/${id}`, { notes, health_score: parseInt(healthScore, 10) });
      toast.success("Landing page updated");
      setEditing(false);
      load();
    } catch (e) { toast.error("Update failed"); }
  };

  if (!lp) return <Layout title="Loading…"><div className="text-sm text-muted-foreground p-6">Loading landing page…</div></Layout>;

  const s = lp.summary || {};

  return (
    <Layout title={lp.title} subtitle={lp.client?.name}>
      <Link to="/landing-pages" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-lps">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to Landing Pages
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="space-y-4">
          <div className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-center gap-2 mb-3"><Globe className="w-4 h-4 text-muted-foreground" /><div className="label-mono">URL</div></div>
            <a href={`https://${lp.url}`} target="_blank" rel="noreferrer" className="text-sm text-primary hover:underline break-all flex items-center gap-1">{lp.url} <ExternalLink className="w-3 h-3" /></a>
            <dl className="mt-4 text-sm space-y-2">
              <div className="flex justify-between"><dt className="text-muted-foreground">Client</dt><dd>{lp.client?.name || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Updated</dt><dd>{formatDate(lp.last_updated)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Linked Campaigns</dt><dd>{lp.linked_campaigns?.length || 0}</dd></div>
            </dl>
          </div>

          <div className="bg-card border border-border rounded-sm p-5" data-testid="lp-health">
            <div className="flex items-center justify-between mb-3">
              <div className="label-mono">Health Score</div>
              <button onClick={() => setEditing(!editing)} className="text-xs text-primary hover:underline"><Edit2 className="w-3 h-3 inline mr-1" /> Edit</button>
            </div>
            {editing ? (
              <input type="number" min="0" max="100" value={healthScore} onChange={(e) => setHealthScore(e.target.value)} className="w-full h-10 px-3 border border-border rounded-sm text-sm mb-3" />
            ) : (
              <div className="font-heading text-4xl font-light tabular mb-3">{lp.health_breakdown?.overall || lp.health_score}</div>
            )}
            <div className="space-y-1.5 text-xs">
              <BreakdownRow label="Conversion" value={lp.health_breakdown?.conversion} />
              <BreakdownRow label="Traffic Quality" value={lp.health_breakdown?.traffic_quality} />
              <BreakdownRow label="Freshness" value={lp.health_breakdown?.freshness} />
            </div>
          </div>

          <div className="bg-card border border-border rounded-sm p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="label-mono">Notes</div>
              <button onClick={() => (editing ? save() : setEditing(true))} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="lp-notes-edit">
                {editing ? <><Save className="w-3 h-3" /> Save</> : <><Edit2 className="w-3 h-3" /> Edit</>}
              </button>
            </div>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} disabled={!editing} placeholder="Add context, experiments, learnings…" className="w-full h-24 px-3 py-2 border border-border rounded-sm text-sm disabled:bg-secondary/20" />
          </div>
        </div>

        <div className="lg:col-span-2 space-y-4">
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-sm p-4 kpi-border-primary"><div className="label-mono">Clicks · 7d</div><div className="font-heading text-2xl font-light mt-2 tabular">{s.clicks_7d || 0}</div></div>
            <div className="bg-card border border-border rounded-sm p-4 kpi-border-success"><div className="label-mono">Leads · 7d</div><div className="font-heading text-2xl font-light mt-2 tabular">{s.leads_7d || 0}</div></div>
            <div className="bg-card border border-border rounded-sm p-4 kpi-border-ai"><div className="label-mono">Conv Rate · 7d</div><div className="font-heading text-2xl font-light mt-2 tabular">{formatPct(s.cvr_7d, 2)}</div></div>
          </div>

          <div className="bg-card border border-border rounded-sm p-5">
            <div className="label-mono mb-3">Performance · 30 days</div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={lp.trend_30d}>
                <CartesianGrid strokeDasharray="2 4" stroke="hsl(220 15% 90%)" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
                <YAxis yAxisId="l" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
                <YAxis yAxisId="r" orientation="right" tick={{ fontSize: 10, fill: "hsl(220 15% 40%)" }} />
                <Tooltip contentStyle={{ borderRadius: 4, border: "1px solid hsl(220 15% 90%)", fontSize: 12 }} />
                <Line yAxisId="l" type="monotone" dataKey="clicks" stroke="hsl(214 100% 35%)" strokeWidth={2} dot={false} name="Clicks" />
                <Line yAxisId="r" type="monotone" dataKey="leads" stroke="hsl(150 70% 30%)" strokeWidth={2} dot={false} name="Leads" />
                <Line yAxisId="r" type="monotone" dataKey="cvr" stroke="hsl(275 60% 50%)" strokeWidth={1.5} strokeDasharray="4 4" dot={false} name="CVR %" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-card border border-border rounded-sm p-5">
            <div className="label-mono mb-3">Linked Campaigns</div>
            {lp.linked_campaigns.map((c) => (
              <Link key={c.id} to={`/campaigns/${c.id}`} className="flex items-center justify-between py-2 border-b border-border last:border-b-0 hover:bg-secondary/30 -mx-2 px-2 rounded-sm">
                <div className="text-sm font-medium">{c.name}</div>
                <StatusBadge tone={statusColor(c.status)}>{c.status}</StatusBadge>
              </Link>
            ))}
            {lp.linked_campaigns.length === 0 && <div className="text-xs text-muted-foreground">No campaigns linked to this landing page.</div>}
          </div>

          {lp.ai_suggestions?.length > 0 && (
            <div className="bg-card border border-border border-t-2 border-t-[hsl(var(--ai))] rounded-sm p-5" data-testid="lp-ai-suggestions">
              <div className="flex items-center gap-2 mb-3"><Sparkles className="w-4 h-4 text-[hsl(var(--ai))]" /><div className="label-mono text-[hsl(var(--ai))]">AI Suggestions</div></div>
              <div className="space-y-2">
                {lp.ai_suggestions.map((s, i) => (
                  <div key={i} className={`border-l-2 pl-3 py-1 ${s.severity === "critical" ? "border-[hsl(var(--critical))]" : s.severity === "warning" ? "border-[hsl(var(--warning))]" : "border-[hsl(var(--primary))]"}`}>
                    <div className="text-sm font-medium">{s.title}</div>
                    <div className="text-xs text-muted-foreground">{s.detail}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {lp.tasks?.length > 0 && (
            <div className="bg-card border border-border rounded-sm p-5">
              <div className="label-mono mb-3">Linked Tasks</div>
              {lp.tasks.map((t) => (
                <div key={t.id} className="py-2 border-b border-border last:border-b-0 flex justify-between">
                  <div className="text-sm">{t.title}</div>
                  <StatusBadge tone={t.status === "done" ? "success" : "primary"}>{t.status}</StatusBadge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}

function BreakdownRow({ label, value }) {
  const val = Math.max(0, Math.min(100, value || 0));
  const tone = val >= 70 ? "bg-[hsl(var(--success))]" : val >= 40 ? "bg-[hsl(var(--warning))]" : "bg-[hsl(var(--critical))]";
  return (
    <div>
      <div className="flex justify-between"><span className="text-muted-foreground">{label}</span><span className="font-semibold tabular">{val}</span></div>
      <div className="h-1 bg-secondary rounded-sm overflow-hidden mt-0.5"><div className={`h-full ${tone}`} style={{ width: `${val}%` }} /></div>
    </div>
  );
}
